import { auth } from "./firebase";
import { storage } from "./firebase";
import { firebaseConfig } from "../firebase.config";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

const FUNCTIONS_REGION = "us-central1";
const DEFAULT_BASE_URL = `https://${FUNCTIONS_REGION}-${firebaseConfig.projectId}.cloudfunctions.net`;
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_BASE_URL || DEFAULT_BASE_URL;

async function getIdToken() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User must be signed in");
  }
  return user.getIdToken();
}

async function callFunction(endpoint, { method = "POST", body } = {}) {
  const idToken = await getIdToken();
  const options = {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  };

  if (body && method !== "GET") {
    options.headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }

  const response = await fetch(`${FUNCTIONS_BASE_URL}/${endpoint}`, options);
  if (!response.ok) {
    let errorMessage = `Request to ${endpoint} failed`;
    try {
      const errorBody = await response.json();
      if (errorBody?.error) {
        errorMessage = errorBody.error;
      }
    } catch (err) {
      // ignore JSON parse errors
    }
    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return {};
  }

  const contentType = response.headers.get("Content-Type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }
  return response.text();
}

export function createSeed(tiktokUrl) {
  if (!tiktokUrl) {
    throw new Error("tiktok_url is required");
  }
  return callFunction("createSeed", {
    body: { tiktok_url: tiktokUrl.trim() },
  });
}

export function deleteSeed(seedId) {
  if (!seedId) {
    throw new Error("seed_id is required");
  }
  return callFunction("deleteSeed", {
    body: { seed_id: seedId },
  });
}

export function toggleSeedStar(seedId, starred) {
  if (!seedId) {
    throw new Error("seed_id is required");
  }
  return callFunction("toggleSeedStar", {
    body: { seed_id: seedId, starred: Boolean(starred) },
  });
}

export function createProject({
  seedId,
  startSeconds,
  endSeconds,
  cleanExtraPrompt,
  cleanActorUrl,
  cleanModel,
}) {
  if (!seedId) {
    throw new Error("seed_id is required");
  }
  return callFunction("createProject", {
    body: {
      seed_id: seedId,
      start_seconds: startSeconds,
      end_seconds: endSeconds,
      clean_extra_prompt: cleanExtraPrompt || "",
      clean_actor_url: cleanActorUrl || null,
      clean_model: cleanModel || "ultra",
    },
  });
}

export function deleteProject(projectId) {
  if (!projectId) {
    throw new Error("project_id is required");
  }
  return callFunction("deleteProject", {
    body: { project_id: projectId },
  });
}

export function deleteVersion(versionId) {
  if (!versionId) {
    throw new Error("version_id is required");
  }
  return callFunction("deleteVersion", {
    body: { version_id: versionId },
  });
}

export function remixProject(versionId, remixPrompt, additionalImageUrls = [], quality = "pro") {
  if (!versionId || !remixPrompt) {
    throw new Error("version_id and remix_prompt are required");
  }
  return callFunction("remixProject", {
    body: {
      version_id: versionId,
      remix_prompt: remixPrompt,
      additional_image_urls: additionalImageUrls,
      remix_quality: quality,
    },
  });
}

export function renderProject(versionId, videoPrompt, renderQuality = "default") {
  if (!versionId) {
    throw new Error("version_id is required");
  }
  const body = { version_id: versionId, render_quality: renderQuality };
  if (videoPrompt && videoPrompt.trim()) {
    body.video_prompt = videoPrompt.trim();
  }
  return callFunction("renderProject", { body });
}

export function deleteVideo(videoId) {
  if (!videoId) {
    throw new Error("video_id is required");
  }
  return callFunction("deleteVideo", {
    body: { video_id: videoId },
  });
}

export function dupeVideo({
  sourceVideoId,
  cleanExtraPrompt,
  cleanActorUrl,
  cleanModel,
}) {
  if (!sourceVideoId) {
    throw new Error("source_video_id is required");
  }
  return callFunction("dupeVideo", {
    body: {
      source_video_id: sourceVideoId,
      clean_extra_prompt: cleanExtraPrompt || "",
      clean_actor_url: cleanActorUrl || null,
      clean_model: cleanModel || "ultra",
    },
  });
}

export function copyVideo({
  sourceVideoId,
  cleanExtraPrompt,
  cleanActorUrl,
  cleanModel,
}) {
  if (!sourceVideoId) {
    throw new Error("source_video_id is required");
  }
  return callFunction("copyVideo", {
    body: {
      source_video_id: sourceVideoId,
      clean_extra_prompt: cleanExtraPrompt || "",
      clean_actor_url: cleanActorUrl || null,
      clean_model: cleanModel || "ultra",
    },
  });
}

export function listVideos() {
  return callFunction("listVideos", { method: "GET" });
}

export function updateVideoNotes(videoId, notes) {
  if (!videoId) {
    throw new Error("video_id is required");
  }
  return callFunction("updateVideoNotes", {
    body: { video_id: videoId, notes: notes || "" },
  });
}

export function retryVideo(videoId) {
  if (!videoId) {
    throw new Error("video_id is required");
  }
  return callFunction("retryVideo", {
    body: { video_id: videoId },
  });
}

export function copyPromptFromVideo(versionId) {
  if (!versionId) {
    throw new Error("version_id is required");
  }
  return callFunction("copyPromptFromVideo", {
    body: { version_id: versionId },
  });
}

export function enhanceProjectPrompt(versionId, basePrompt) {
  if (!versionId || !basePrompt) {
    throw new Error("version_id and base_prompt are required");
  }
  return callFunction("enhanceProjectPrompt", {
    body: {
      version_id: versionId,
      base_prompt: basePrompt,
    },
  });
}

export function retryProject(versionId, remixQuality = "pro") {
  if (!versionId) {
    throw new Error("version_id is required");
  }
  return callFunction("retryProject", {
    body: { version_id: versionId, remix_quality: remixQuality },
  });
}

export async function addActorImages(files) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User must be signed in");
  }
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error("At least one image is required");
  }

  const uploads = files.map(async (file, index) => {
    const safeName = (file.name || `actor_${index}.png`).replace(/[^a-zA-Z0-9._-]/g, "_");
    const objectPath = `user_data/${user.uid}/actors/${Date.now()}_${index}_${safeName}`;
    const objectRef = storageRef(storage, objectPath);
    await uploadBytes(objectRef, file, { contentType: file.type || "image/png" });
    return getDownloadURL(objectRef);
  });

  return Promise.all(uploads);
}

export function deleteActorByPath(fullPath) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User must be signed in");
  }
  if (!fullPath) {
    throw new Error("fullPath is required");
  }
  if (!fullPath.startsWith(`user_data/${user.uid}/actors/`)) {
    throw new Error("Invalid actor path");
  }
  const objectRef = storageRef(storage, fullPath);
  return deleteObject(objectRef);
}

export async function generateActorsFromImages(files, count = 1) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User must be signed in");
  }
  if (!Array.isArray(files) || files.length === 0) {
    throw new Error("At least one image is required");
  }

  const parsedCount = Number(count);
  if (!Number.isInteger(parsedCount) || parsedCount < 1 || parsedCount > 20) {
    throw new Error("count must be between 1 and 20");
  }

  const requestId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const tempUploads = files.map(async (file, index) => {
    const safeName = (file.name || `temp_${index}.png`).replace(/[^a-zA-Z0-9._-]/g, "_");
    const objectPath = `user_data/${user.uid}/actors_temp/${requestId}/${Date.now()}_${index}_${safeName}`;
    const objectRef = storageRef(storage, objectPath);
    await uploadBytes(objectRef, file, { contentType: file.type || "image/png" });
    return objectRef.fullPath;
  });

  const tempImagePaths = await Promise.all(tempUploads);
  return callFunction("generateActors", {
    body: {
      temp_image_paths: tempImagePaths,
      count: parsedCount,
    },
  });
}
